<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <form
      data-qa="prescriber-information"
      class="kds-main-wrapper"
      autocomplete="off"
    >
      <p
        class="kds-typography-title-xlarge"
        data-qa="header"
        v-html="$t('eu__prescriber_information__header')"
      />
      <div class="kds-content-block">
        <TextField
          :label="$t('eu__prescriber_information__prescriber_name')"
          data-qa="prescriber-name"
          v-bind="fieldAttrs.prescriberName"
        />
        <TextField
          :label="$t('eu__prescriber_information__place')"
          data-qa="place"
          v-bind="fieldAttrs.place"
        />
        <TextField
          :label="$t('eu__prescriber_information__zipcode')"
          data-qa="zipcode"
          v-bind="fieldAttrs.zipCode"
          @input="filterZipCodeInputEvent"
        />
      </div>
      <div class="kds-content-block">
        <BaseButton
          :text="$t('generic_action_continue')"
          data-qa="submit"
          v-bind="submitAttrs"
        />
        <BaseButton
          variant="outlined"
          :text="$t('generic_skip')"
          data-qa="cancel"
          @click="handleCancelEvent"
        />
      </div>
    </form>
  </ResponsiveLayout>
</template>

<script setup>
import { onMounted, ref } from 'vue'

import { sendHcpInformation } from '@shared/api-endpoints'
import useEuStore from '@eu/app/stores/useEuStore.js'
import BaseButton from '@shared/components/BaseButton.vue'
import Tracker from '@shared/Tracker'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import TextField from '@shared/components/form/TextField.vue'
import useForm from '@shared/composables/useForm.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'

const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.PRESCRIBER_INFORMATION,
)

// Shown only once per session: PRESCRIPTION_INSURANCE's next and PHONE_INPUT's
// back read this flag from the navigation ctx
onMounted(() => {
  useEuStore().prescriberInformationShown = true
})
const form = ref({
  prescriberName: '',
  place: '',
  zipCode: '',
})

const { fieldAttrs, submitAttrs } = useForm(
  form,
  ref({
    prescriberName: {
      validations: ['required', 'minLength:3', 'maxLength:256'],
    },
    place: { validations: ['required'] },
    zipCode: { validations: [] },
  }),
  {
    onSubmit: async () => {
      return sendHcpInformation({
        name: form.value.prescriberName?.trim(),
        city: form.value.place?.trim(),
        zip: form.value.zipCode?.trim(),
      })
    },
    onSuccess: async () => {
      Tracker.trackButtonSelect('continue', 'signup_hcp_info')
      // in-app users are returned into the app by the download-link guard
      await navigateNext()
    },
    onError: async (e) => {
      console.error(e)
      return {
        prescriberName: true,
        place: true,
        zipCode: 'generic__error_title',
      }
    },
  },
)

const filterZipCodeInputEvent = (event) => {
  const chars = event.target.value
    .split('')
    .filter((c) => (c >= 0 && c <= 9) || c === ' ' || c === '-')
  event.target.value = form.value.zipCode = chars.join('')
}

const handleCancelEvent = async () => {
  Tracker.trackButtonSelect('skip', 'signup_hcp_info')
  await navigateNext()
}
</script>
